/**
 * Admin Role Class
 * Implements admin-specific behaviors and permissions
 */

import { BaseRole, NavigationItem, QuickAction, DashboardMetric } from './BaseRole';

export class AdminRole extends BaseRole {
  get roleName(): string {
    return 'ADMIN';
  }

  get displayName(): string {
    return this.user.isSupport ? 'Support Agent' : 'Administrator';
  }

  getDefaultRoute(): string {
    return '/admin';
  }

  getNavigationItems(): NavigationItem[] {
    const items: NavigationItem[] = [];
    
    items.push({
      label: 'Dashboard',
      path: '/admin',
      icon: 'LayoutDashboard'
    });
    
    if (this.user.isAdmin) {
      items.push({
        label: 'Users',
        path: '/admin/users',
        icon: 'Users',
        children: [
          { label: 'All Users', path: '/admin/users' },
          { label: 'Merchants', path: '/admin/merchants' },
          { label: 'Roles & Permissions', path: '/admin/roles' }
        ]
      });
      
      items.push({
        label: 'Transactions',
        path: '/admin/transactions',
        icon: 'Receipt'
      });
      
      items.push({
        label: 'Analytics',
        path: '/admin/analytics',
        icon: 'TrendingUp',
        children: [
          { label: 'Overview', path: '/admin/analytics' },
          { label: 'Revenue', path: '/admin/analytics/revenue' },
          { label: 'User Growth', path: '/admin/analytics/growth' }
        ]
      });
      
      items.push({
        label: 'System',
        path: '/admin/system',
        icon: 'Settings',
        children: [
          { label: 'Configuration', path: '/admin/config' },
          { label: 'Currencies', path: '/admin/currencies' },
          { label: 'API Keys', path: '/admin/api-keys' },
          { label: 'Audit Logs', path: '/admin/audit' }
        ]
      });
    }
    
    // Support-specific items
    if (this.user.isSupport || this.user.isAdmin) {
      items.push({
        label: 'Support',
        path: '/admin/support',
        icon: 'Headphones',
        children: [
          { label: 'Tickets', path: '/admin/support/tickets' },
          { label: 'FAQ Management', path: '/admin/support/faq' },
          { label: 'User Issues', path: '/admin/support/issues' }
        ]
      });
    }
    
    items.push({
      label: 'Reports',
      path: '/admin/reports',
      icon: 'FileText'
    });
    
    return items;
  }

  canPerformAction(action: string): boolean {
    // Support agents have limited permissions
    if (this.user.isSupport && !this.user.isAdmin) {
      const supportPermissions: Record<string, boolean> = {
        'view_users': true,
        'view_transactions': true,
        'view_support_tickets': true,
        'manage_support_tickets': true,
        'view_user_details': true,
        'reset_user_password': true,
        'view_reports': true,
        'export_data': false,
        'modify_users': false,
        'delete_users': false,
        'manage_system': false,
        'manage_currencies': false,
        'manage_api_keys': false
      };
      
      return supportPermissions[action] || false;
    }
    
    // Full admins have all permissions
    const adminPermissions: Record<string, boolean> = {
      'view_users': true,
      'modify_users': true,
      'delete_users': true,
      'manage_roles': true,
      'view_transactions': true,
      'modify_transactions': true,
      'view_analytics': true,
      'manage_system': true,
      'manage_currencies': true,
      'manage_api_keys': true,
      'view_audit_logs': true,
      'export_data': true,
      'impersonate_user': true,
      'manage_support_tickets': true,
      'manage_merchants': true,
      'approve_merchants': true,
      'suspend_users': true,
      'manage_referrals': true,
      'manage_notifications': true
    };
    
    return adminPermissions[action] || false;
  }

  getBadgeColor(): string {
    return this.user.isAdmin ? 'danger' : 'warning';
  }

  getIconName(): string {
    return this.user.isAdmin ? 'Shield' : 'Headphones';
  }

  getWelcomeMessage(): string {
    const role = this.user.isAdmin ? 'Administrator' : 'Support Agent';
    return `${role} Dashboard - ${this.user.displayName}`;
  }

  getQuickActions(): QuickAction[] {
    const actions: QuickAction[] = [];
    
    if (this.user.isAdmin) {
      actions.push({
        id: 'view_users',
        title: 'Manage Users',
        description: 'View and manage platform users',
        icon: 'Users',
        action: '/admin/users'
      });
      
      actions.push({
        id: 'view_transactions',
        title: 'Recent Transactions',
        description: 'Monitor platform transactions',
        icon: 'Receipt',
        action: '/admin/transactions'
      });
      
      actions.push({
        id: 'system_health',
        title: 'System Health',
        description: 'Check system status',
        icon: 'Activity',
        action: '/admin/system/health'
      });
      
      actions.push({
        id: 'generate_report',
        title: 'Generate Report',
        description: 'Create custom reports',
        icon: 'FileText',
        action: '/admin/reports/new'
      });
    }
    
    if (this.user.isSupport || this.user.isAdmin) {
      actions.push({
        id: 'support_tickets',
        title: 'Support Tickets',
        description: 'View open support tickets',
        icon: 'MessageCircle',
        action: '/admin/support/tickets',
        variant: 'primary'
      });
      
      actions.push({
        id: 'user_lookup',
        title: 'User Lookup',
        description: 'Search for a specific user',
        icon: 'Search',
        action: () => 'open_user_search_modal'
      });
    }
    
    return actions;
  }

  getDashboardMetrics(): DashboardMetric[] {
    const metrics: DashboardMetric[] = [];
    
    if (this.user.isAdmin) {
      metrics.push({
        id: 'total_users',
        label: 'Total Users',
        value: '0',
        icon: 'Users',
        color: 'blue'
      });
      
      metrics.push({
        id: 'active_merchants',
        label: 'Active Merchants',
        value: '0',
        icon: 'Store',
        color: 'green'
      });
      
      metrics.push({
        id: 'daily_transactions',
        label: 'Daily Transactions',
        value: '0',
        icon: 'TrendingUp',
        color: 'purple'
      });
      
      metrics.push({
        id: 'platform_revenue',
        label: 'Platform Revenue',
        value: '$0',
        icon: 'DollarSign',
        color: 'green'
      });
    }
    
    if (this.user.isSupport || this.user.isAdmin) {
      metrics.push({
        id: 'open_tickets',
        label: 'Open Tickets',
        value: '0',
        icon: 'MessageCircle',
        color: 'yellow'
      });
      
      metrics.push({
        id: 'avg_response_time',
        label: 'Avg Response Time',
        value: '0h',
        icon: 'Clock',
        color: 'blue'
      });
    }
    
    return metrics;
  }

  // ===== Admin-specific helper methods =====

  getAccessLevel(): 'full' | 'support' | 'readonly' {
    if (this.user.isAdmin) return 'full';
    if (this.user.isSupport) return 'support';
    return 'readonly';
  }

  canAccessSystemSettings(): boolean {
    return this.user.isAdmin;
  }

  canModifyUserRoles(): boolean {
    return this.user.isAdmin;
  }

  canViewSensitiveData(): boolean {
    return this.user.isAdmin;
  }

  canImpersonateUser(): boolean {
    return this.user.isAdmin && this.canPerformAction('impersonate_user');
  }
}
